import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { nameList } from '../nameList';
import type { BlockedStep } from '../phaseDeletion';

/**
 * The two answers to "delete this phase?", in one place.
 *
 * The steps go with the phase now — they used to survive it, parked in
 * Unassigned, and this dialog existed to warn that those parked steps would
 * block the next publish. With the holding pen gone there is nowhere to park
 * them, so the question changed from "did you know this leaves the workflow
 * unpublishable?" to "did you mean to destroy this work?".
 *
 * Which makes the refusal the more important half. A step a task is standing on
 * cannot be deleted at all — the service refuses the save with
 * `WORKFLOW_STEPS_IN_USE`, and letting the author find that out twenty minutes
 * later, after building on the change, is the failure this dialog is really
 * for. So `blocked` is not an error state bolted on: it is the first thing this
 * component knows how to say.
 *
 * Steps and tasks are named rather than counted, throughout. "3 steps" is a
 * number; "Specify, Draft, Summarise" is the thing the author has to go and
 * look at, and "2 tasks" gives nobody anywhere to start.
 *
 * Never opened for an empty phase. There is nothing to lose and nothing to
 * refuse, so it deletes on the spot — a confirmation with one sensible answer
 * teaches people to click through confirmations.
 */
export function DeletePhaseDialog({
  phaseName,
  stepNames,
  blocked,
  open,
  onOpenChange,
  onConfirm,
}: {
  phaseName: string;
  /** The steps that will be deleted. Empty when `blocked` is non-empty. */
  stepNames: string[];
  /** Steps that cannot go, with the tasks standing on them. */
  blocked?: BlockedStep[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  const isBlocked = blocked !== undefined && blocked.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isBlocked
              ? `${phaseName} still has work on it`
              : `Delete ${phaseName}?`}
          </DialogTitle>
          <DialogDescription>
            {isBlocked ? (
              <>
                Deleting this phase would delete{' '}
                {blocked.length === 1
                  ? 'a step that a task is'
                  : 'steps that tasks are'}{' '}
                sitting on. Move or delete{' '}
                {blocked.length === 1 ? 'that task' : 'those tasks'} on the
                board first, then delete the phase.
              </>
            ) : (
              <>
                {stepNames.length === 1
                  ? 'This deletes its step: '
                  : `This deletes its ${String(stepNames.length)} steps: `}
                {nameList(stepNames)}. Undo restores them until you save.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {isBlocked ? (
          // Named per step, because "move the tasks" is not actionable until
          // you know which board columns to look in.
          <ul className="flex flex-col gap-2 text-sm">
            {blocked.map((entry) => (
              <li key={entry.stepName} className="flex flex-col gap-0.5">
                <span className="font-medium">{entry.stepName}</span>
                <span className="text-muted-foreground text-xs">
                  {nameList(entry.taskTitles)}
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        <DialogFooter>
          {isBlocked ? (
            <DialogClose render={<Button variant="outline" />}>
              Close
            </DialogClose>
          ) : (
            <>
              <DialogClose render={<Button variant="outline" />}>
                Cancel
              </DialogClose>
              <Button
                variant="destructive"
                onClick={() => {
                  onConfirm();
                  onOpenChange(false);
                }}
              >
                Delete phase
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
